import React, { useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import autoTable from "jspdf-autotable";
import { PDFGeneratorContext } from "./PDFGeneratorContext";
import { useAppTheme } from "../../utils/theme";

const PDF_WIDTH = 842; // A4 landscape in pt
const PDF_HEIGHT = 595;

const PDFGeneratorProvider = ({ children }: { children: React.ReactNode }) => {
	const theme = useAppTheme();
	const downloadRef = useRef(null);

	// Captures the downloadRef as a canvas screenshot, then places it in a PDF
	const captureAsPDF = async (): Promise<jsPDF | null> => {
		const input = downloadRef.current as HTMLElement | null;
		if (!input) return null;

		const canvas = await html2canvas(input, {
			scale: 1,
			useCORS: true,
			allowTaint: true,
			backgroundColor: theme.palette.surface.light,
		});

		const imgData = canvas.toDataURL("image/jpeg", 0.7);
		const pdf = new jsPDF("landscape", "pt", "a4");
		pdf.addImage(imgData, "JPEG", 0, 0, PDF_WIDTH, PDF_HEIGHT);
		return pdf;
	};

	const handleBase64Certificate = (callback: (base64: string) => void) => {
		captureAsPDF().then((pdf) => {
			if (pdf) {
				const pdfBase64 = pdf.output("datauristring");
				callback(pdfBase64);
			}
		});
	};

	const handleDownloadCertificate = (fileName: string) => {
		captureAsPDF().then((pdf) => {
			if (pdf) {
				pdf.save(fileName);
			}
		});
	};

	const generateTablePDF = (
		headers: string[],
		// TODO: use same type when defined above
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		data: any[],
		fileName: string,
	) => {
		const doc = new jsPDF("landscape", "pt", "a4");

		autoTable(doc, {
			head: [headers],
			body: data,
		});

		doc.save(fileName);
	};
	return (
		<PDFGeneratorContext.Provider
			value={{
				downloadRef,
				handleDownloadCertificate,
				handleBase64Certificate,
				generateTablePDF,
			}}
		>
			{children}
		</PDFGeneratorContext.Provider>
	);
};

export default PDFGeneratorProvider;
